import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { MapPin, Loader2 } from 'lucide-react'
import './AddressAutocomplete.css'

export default function AddressAutocomplete({
    value,
    onChange,
    onSelect,
    disabled = false,
    placeholder = 'Rechercher une adresse',
    error = false
}) {
    const [inputValue, setInputValue] = useState(value || '')
    const [suggestions, setSuggestions] = useState([])
    const [loading, setLoading] = useState(false)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const wrapperRef = useRef(null)
    const ignoreSearch = useRef(false)

    // Sync input value with prop
    useEffect(() => {
        setInputValue(value || '')
    }, [value])

    // Click outside handler
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const fetchSuggestions = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([])
            return
        }

        setLoading(true)
        try {
            // Using Swiss GeoAdmin API
            const response = await axios.get('https://api3.geo.admin.ch/rest/services/api/SearchServer', {
                params: {
                    searchText: query,
                    type: 'locations',
                    origins: 'address',
                    limit: 10
                }
            })

            setSuggestions(response.data.results || [])
            setShowSuggestions(true)
        } catch (error) {
            console.error('Error fetching address suggestions:', error)
            setSuggestions([])
        } finally {
            setLoading(false)
        }
    }

    // Debounce search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (ignoreSearch.current) {
                // Skip search if we just selected an item
                ignoreSearch.current = false
                return
            }

            if (inputValue.length > 2) {
                fetchSuggestions(inputValue)
            } else {
                setSuggestions([])
                setShowSuggestions(false)
            }
        }, 500) // 500ms debounce

        return () => clearTimeout(timeoutId)
    }, [inputValue])

    const handleInputChange = (e) => {
        const newVal = e.target.value
        ignoreSearch.current = false
        setInputValue(newVal)
        onChange(newVal)
    }

    const handleSelect = (suggestion) => {
        // Remove HTML tags from label
        const rawLabel = suggestion.attrs.label.replace(/<[^>]*>/g, '')

        let parsed = {
            adresse: rawLabel,
            npa: '',
            ville: ''
        }

        // Regex for "Address, NPA Ville" or "Address NPA Ville"
        // Captures: 1=Address, 2=NPA, 3=Ville
        const regex = /^(.*?)(?:,\s*|\s+)(\d{4})\s+(.+)$/
        const match = rawLabel.match(regex)

        if (match) {
            parsed.adresse = match[1].trim()
            parsed.npa = match[2]
            parsed.ville = match[3].trim()
        }

        ignoreSearch.current = true
        setInputValue(parsed.adresse)
        setShowSuggestions(false)

        onSelect(parsed)
    }

    return (
        <div className="address-autocomplete-wrapper" ref={wrapperRef}>
            <div className="input-group">
                <input
                    type="text"
                    className={`form-input ${error ? 'error' : ''}`}
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => inputValue.length > 2 && setShowSuggestions(true)}
                    disabled={disabled}
                />
                {loading && (
                    <div className="input-icon-right">
                        <Loader2 className="spin" size={16} />
                    </div>
                )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
                <ul className="suggestions-list">
                    {suggestions.map((suggestion) => (
                        <li
                            key={suggestion.id}
                            onClick={() => handleSelect(suggestion)}
                            className="suggestion-item"
                        >
                            <MapPin size={14} className="suggestion-icon" />
                            <span dangerouslySetInnerHTML={{ __html: suggestion.attrs.label }} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
